import { useI18n } from '@ecom/i18n';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useId, useLayoutEffect, useRef, useState } from 'react';

import { useDailySalesSeriesQuery } from '../api';

import { CountUp } from '@/components/ui/CountUp';
import { formatCatalogPrice } from '@/features/products/format-price';
import { pluralKey } from '@/lib/plural';

/** A round step (1, 2, 2.5, 5 × 10ⁿ) giving about four intervals, and a top tick strictly above the maximum. */
export function niceScale(max: number): { readonly top: number; readonly step: number } {
  if (max <= 0) return { top: 4, step: 1 };
  const raw = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * magnitude >= raw) ?? 10) * magnitude;
  let top = Math.ceil(max / step) * step;
  if (top <= max) top += step;
  return { top, step };
}

/** Points are local calendar days (`YYYY-MM-DD`): read at noon so no time zone moves them across midnight. */
function dayOf(date: string): Date {
  return new Date(`${date}T12:00:00`);
}

function intlLocale(locale: string): string {
  if (locale === 'ar') return 'ar-TN';
  if (locale === 'en') return 'en-GB';
  return 'fr-TN';
}

/** The plot's vertical frame, in the SVG's 0–100 units: the line never touches the top edge. */
const BASE_Y = 92;
const SPAN_Y = 76;

/**
 * The entrance: a clip wipes the line and its gradient in from the first day to today, a glowing tip rides the line's
 * tip, and each day sends a ripple the moment the tip reaches it. One easing for all three, so they stay in step.
 */
const DRAW_MS = 1_400;
const DRAW_BEZIER = [0.45, 0, 0.2, 1] as const;
const DRAW_EASING = `cubic-bezier(${DRAW_BEZIER.join(', ')})`;

function motionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * When, as a fraction of DRAW_MS, the wipe reaches a given fraction of the width: the inverse of DRAW_EASING, solved by
 * bisection on the curve's parameter (the progress is monotonic, so this is exact to the pixel).
 */
export function drawTimeAt(progress: number): number {
  const [x1, y1, x2, y2] = DRAW_BEZIER;
  const bezier = (a: number, b: number, s: number): number =>
    3 * a * s * (1 - s) ** 2 + 3 * b * s ** 2 * (1 - s) + s ** 3;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (bezier(y1, y2, mid) < progress) low = mid;
    else high = mid;
  }
  return bezier(x1, x2, (low + high) / 2);
}

/**
 * « Évolution des ventes » in the Figma design (« Souk 2.0 »): the selected period's real orders (7, 14 or 30 days,
 * cancelled ones excluded like every revenue figure on the dashboard), drawn as a blue line over a soft gradient on
 * dashed gridlines, drawn in from the first day to today (see DRAW_MS). Each day is a point you can hover or reach with the keyboard; it shows a tooltip with the day, its
 * revenue and its orders. The period total sits in the header. Time runs left to right in every language, as on a
 * calendar. Hand-rolled SVG, no chart dependency.
 */
export function SalesTrendChart({ days }: { days: number }): ReactElement {
  const { t, locale } = useI18n();
  const series = useDailySalesSeriesQuery(days);
  const [active, setActive] = useState<number | null>(null);
  // Where the hover cursor stands: it stays put while it fades out, so it glides from there on the next hover.
  const [cursor, setCursor] = useState(0);
  const titleId = useId();
  const gradientId = useId();
  const clipId = useId();
  const wipeRef = useRef<SVGRectElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const intl = intlLocale(locale);
  // « 14 jours » / « 14 يومًا »: the period, worded by the language's own plural rules.
  const daysLabel = t(pluralKey(locale, days, 'merchant.dashboard.rangeDays'), { count: days });

  const points = series.points;
  const totalRevenue = points.reduce((sum, p) => sum + p.revenue, 0);
  const max = Math.max(...points.map((p) => p.revenue), 0);
  const { top } = niceScale(max);
  const lastIndex = points.length - 1;
  const x = (index: number): number => (lastIndex <= 0 ? 0 : (index / lastIndex) * 100);
  const y = (revenue: number): number => BASE_Y - (revenue / top) * SPAN_Y;
  const line = points.map((p, i) => `${x(i).toFixed(2)},${y(p.revenue).toFixed(2)}`).join(' ');
  const area = `0,${String(BASE_Y)} ${line} 100,${String(BASE_Y)}`;
  // A new period or new figures replay the entrance; a refetch with the same figures does not.
  const revealKey = `${String(days)}:${line}`;
  const lastPoint = points[lastIndex];

  useLayoutEffect(() => {
    const wipe = wipeRef.current;
    const tip = tipRef.current;
    if (wipe === null || tip === null || !motionAllowed() || typeof wipe.animate !== 'function') {
      return undefined;
    }
    const timing = { duration: DRAW_MS, easing: DRAW_EASING };
    const animations = [
      wipe.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], timing),
      // Linear between keyframes, eased as a whole: the tip is exactly where the wipe is, on the line.
      tip.animate(
        points.map((p, i) => ({
          left: `${String(x(i))}%`,
          top: `${String(y(p.revenue))}%`,
          offset: x(i) / 100,
        })),
        timing,
      ),
      tip.animate(
        [{ opacity: 0 }, { opacity: 1, offset: 0.06 }, { opacity: 1, offset: 0.9 }, { opacity: 0 }],
        { duration: DRAW_MS + 200, easing: 'ease-out' },
      ),
    ];
    return () => {
      for (const animation of animations) animation.cancel();
    };
    // `revealKey` names everything the keyframes are built from.
  }, [revealKey]);

  const focusPoint = (index: number): void => {
    setActive(index);
    setCursor(index);
  };

  const dayFormat = new Intl.DateTimeFormat(intl, { day: '2-digit', month: 'short' });
  const longFormat = new Intl.DateTimeFormat(intl, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  // Four labels on the axis, evenly spread from the first day to today.
  const axisIndexes =
    lastIndex > 0
      ? [0, Math.round(lastIndex / 3), Math.round((2 * lastIndex) / 3), lastIndex]
      : [0];

  return (
    <section aria-labelledby={titleId} className="sk-panel sk-chart-panel flex h-full flex-col">
      <div className="sk-section-head sk-chart-head">
        <div>
          <span className="sk-eyebrow">{t('merchant.dashboard.salesEyebrow')}</span>
          <h2 id={titleId}>{t('merchant.dashboard.salesTrend.title')}</h2>
          <p>{t('merchant.dashboard.salesTrend.subtitleRange', { days: daysLabel })}</p>
        </div>
        <div className="sk-chart-total">
          <span>{t('merchant.dashboard.salesTrend.totalRange', { days: daysLabel })}</span>
          <strong>
            {series.isPending ? '…' : <CountUp value={formatCatalogPrice(totalRevenue)} />}
          </strong>
        </div>
      </div>

      {series.isPending ? (
        <div className="ecom-skeleton mt-5 h-[264px] w-full rounded-xl" aria-hidden="true" />
      ) : series.isError ? (
        <p className="mt-5 rounded-xl border border-dashed border-[var(--sk-line-strong)] px-4 py-3 text-sm text-inkSoft">
          {t('merchant.dashboard.salesTrend.loadError')}
        </p>
      ) : totalRevenue === 0 ? (
        <div className="mt-5">
          <EmptyState
            title={t('merchant.dashboard.salesTrend.emptyTitle')}
            description={t('merchant.dashboard.salesTrend.emptyDescription')}
          />
        </div>
      ) : (
        <>
          <div className="sk-legend">
            <span>
              <i aria-hidden="true" /> {t('merchant.dashboard.salesLegend')}
            </span>
          </div>
          <div
            key={revealKey}
            className="sk-chart-wrap"
            dir="ltr"
            onMouseLeave={() => {
              setActive(null);
            }}
          >
            <svg
              className="sk-chart-svg"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label={t('merchant.dashboard.salesTrend.chartAria')}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
                </linearGradient>
                <clipPath id={clipId}>
                  <rect ref={wipeRef} x="0" y="-10" width="100" height="120" />
                </clipPath>
              </defs>
              {[20, 40, 60, 80].map((gy, index) => (
                <line
                  key={gy}
                  x1="0"
                  y1={gy}
                  x2="100"
                  y2={gy}
                  className="sk-gridline"
                  style={{ animationDelay: `${String(index * 70)}ms` }}
                />
              ))}
              <g clipPath={`url(#${clipId})`}>
                <polygon className="sk-chart-area" points={area} fill={`url(#${gradientId})`} />
                <polyline className="sk-chart-line" points={line} />
              </g>
            </svg>
            <div className="sk-chart-points">
              <span
                aria-hidden="true"
                className="sk-chart-cursor"
                data-visible={active !== null}
                style={{ left: `${String(x(cursor))}%` }}
              />
              <span
                aria-hidden="true"
                className="sk-chart-lens"
                data-visible={active !== null}
                style={{
                  left: `${String(x(cursor))}%`,
                  top: `${String(y(points[cursor]?.revenue ?? 0))}%`,
                }}
              />
              {/* Only days that sold ripple: a row of rings on the baseline would just be noise. */}
              {points.map((point, index) =>
                point.revenue > 0 ? (
                  <span
                    key={point.date}
                    aria-hidden="true"
                    className="sk-chart-ripple"
                    style={{
                      left: `${String(x(index))}%`,
                      top: `${String(y(point.revenue))}%`,
                      animationDelay: `${String(Math.round(drawTimeAt(x(index) / 100) * DRAW_MS))}ms`,
                    }}
                  />
                ) : null,
              )}
              {lastPoint === undefined ? null : (
                <span
                  aria-hidden="true"
                  className="sk-chart-now"
                  style={{ left: '100%', top: `${String(y(lastPoint.revenue))}%` }}
                />
              )}
              <span ref={tipRef} aria-hidden="true" className="sk-chart-tip" />
              {points.map((point, index) => (
                <button
                  key={point.date}
                  type="button"
                  data-active={active === index}
                  style={{ left: `${String(x(index))}%`, top: `${String(y(point.revenue))}%` }}
                  onMouseEnter={() => {
                    focusPoint(index);
                  }}
                  onFocus={() => {
                    focusPoint(index);
                  }}
                  onBlur={() => {
                    setActive(null);
                  }}
                  aria-label={`${longFormat.format(dayOf(point.date))}, ${formatCatalogPrice(point.revenue)}`}
                >
                  {active === index ? (
                    <span className="sk-chart-tooltip">
                      <strong>{formatCatalogPrice(point.revenue)}</strong>
                      <small>
                        {longFormat.format(dayOf(point.date))} ·{' '}
                        {t(
                          pluralKey(
                            locale,
                            point.orderCount,
                            'merchant.dashboard.salesTrend.dayOrders',
                          ),
                          {
                            count: point.orderCount,
                          },
                        )}
                      </small>
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            <div className="sk-chart-axis" aria-hidden="true">
              {axisIndexes.map((index) => {
                const point = points[index];
                return point === undefined ? null : (
                  <span key={point.date}>{dayFormat.format(dayOf(point.date))}</span>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
